/** Gross wage for a date range. Not a payslip: no tax, no social payment. */

export type PayType = 'hourly' | 'shift' | 'monthly';

export interface PunchSpan {
  clockIn: Date;
  clockOut: Date | null;
  breakMinutes: number;
}

/** A forgotten clock-out should not invent days of pay. */
export const MAX_PUNCH_MINUTES = 16 * 60;

export interface RangeWage {
  minutes: number;
  shifts: number;
  wageAmd: number;
}

/** One pay rate, in force from `effectiveFrom` (local YYYY-MM-DD) until the next one. */
export interface RatePeriod {
  effectiveFrom: string;
  payType: PayType;
  rateAmd: number;
}

/** The rate in force on a local calendar day, or null when none starts on or before it. */
export function rateOn(rates: readonly RatePeriod[], day: string): RatePeriod | null {
  let best: RatePeriod | null = null;
  for (const rate of rates) {
    if (rate.effectiveFrom <= day && (!best || rate.effectiveFrom > best.effectiveFrom)) best = rate;
  }
  return best;
}

function localDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** Current monthly salary, split by how many days of each month the range covers. */
export function monthlyWageAmd(rateAmd: number, from: Date, to: Date): number {
  if (rateAmd <= 0) return 0;
  let cursor = startOfDay(from);
  const end = startOfDay(to);
  if (cursor > end) return 0;
  let total = 0;
  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const monthEnd = new Date(year, month + 1, 0);
    const sliceEnd = monthEnd < end ? monthEnd : end;
    const days = Math.round((sliceEnd.getTime() - cursor.getTime()) / 86_400_000) + 1;
    total += (rateAmd * days) / daysInMonth(year, month);
    cursor = new Date(year, month + 1, 1);
  }
  return Math.round(total);
}

function overlapMinutes(punch: PunchSpan, from: Date, to: Date, now: Date): number {
  const start = punch.clockIn > from ? punch.clockIn : from;
  const rawEnd = punch.clockOut ?? now;
  const end = rawEnd < to ? rawEnd : to;
  if (end <= start) return 0;
  const mins = Math.round((end.getTime() - start.getTime()) / 60_000) - punch.breakMinutes;
  return Math.min(MAX_PUNCH_MINUTES, Math.max(0, mins));
}

/**
 * Hours always count. Pay uses the rate in force on each local day:
 * hourly on the overlap, one shift when the clock-in day is per-shift,
 * and a monthly salary as that day's fraction of the month.
 */
export function wageForRange(
  rates: readonly RatePeriod[],
  punches: readonly PunchSpan[],
  from: Date,
  to: Date,
  now: Date,
): RangeWage {
  let minutes = 0;
  let shifts = 0;
  let raw = 0;
  for (const punch of punches) {
    const mins = overlapMinutes(punch, from, to, now);
    minutes += mins;
    const rate = rateOn(rates, localDay(punch.clockIn));
    if (!rate) continue;
    if (rate.payType === 'hourly') raw += (mins / 60) * rate.rateAmd;
    else if (rate.payType === 'shift' && punch.clockIn >= from && punch.clockIn <= to) {
      shifts += 1;
      raw += rate.rateAmd;
    }
  }
  let cursor = startOfDay(from);
  const end = startOfDay(to);
  while (cursor <= end) {
    const rate = rateOn(rates, localDay(cursor));
    if (rate?.payType === 'monthly' && rate.rateAmd > 0) {
      raw += rate.rateAmd / daysInMonth(cursor.getFullYear(), cursor.getMonth());
    }
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
  }
  return { minutes, shifts, wageAmd: Math.round(raw) };
}

/**
 * Split a dram pool by weight (hours). Shares are whole dram and add up to the
 * rounded pool. People with no weight get nothing.
 */
export function splitAmount(
  total: number,
  weights: readonly { id: string; weight: number }[],
): Map<string, number> {
  const pool = Math.max(0, Math.round(total));
  const out = new Map<string, number>();
  for (const row of weights) out.set(row.id, 0);
  const active = weights.filter((row) => row.weight > 0);
  const sum = active.reduce((acc, row) => acc + row.weight, 0);
  if (pool === 0 || sum <= 0) return out;
  const parts = active.map((row) => {
    const exact = (pool * row.weight) / sum;
    const base = Math.floor(exact);
    return { id: row.id, base, frac: exact - base };
  });
  let used = parts.reduce((acc, part) => acc + part.base, 0);
  parts.sort((a, b) => b.frac - a.frac || b.base - a.base);
  for (const part of parts) {
    if (used >= pool) break;
    part.base += 1;
    used += 1;
  }
  for (const part of parts) out.set(part.id, part.base);
  return out;
}

export function formatHours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}
